/* globals jQuery */

const DEFAULT_FILTERS_WRAPPER_ID = 'article-overview-filters-wrapper';
const DEFAULT_FILTERS_DESKTOP_SLOT_ID = 'article-overview-filters-desktop-slot';
const DEFAULT_MOBILE_OVERLAY_ID = 'filter-mobile-overlay';
const DEFAULT_MOBILE_FACET_MOUNT_ID = 'filter-mobile-facet-mount';

/**
 * Shared FacetWP overview filter UI (desktop panels, mobile overlay, counts).
 *
 * @param {Object} config
 * @param {string} config.sectionSelector Root section selector (must exist on the page).
 * @param {string[]} config.filterTargets data-target values used for per-filter badge counts.
 * @param {Array<{param: string, target: string}>} config.urlPanelMappings URL query fragments → panel targets.
 * @param {boolean} [config.bindMobileSearch=false] Bind .filter-mobile-open-search to focus the search field.
 * @param {string} [config.filtersWrapperId]
 * @param {string} [config.filtersDesktopSlotId]
 * @param {string} [config.mobileOverlayId]
 * @param {string} [config.mobileFacetMountId]
 */
export function initFacetOverviewFilters( config ) {
	const sectionSelector = config.sectionSelector;
	const filterTargets = config.filterTargets;
	const urlPanelMappings = config.urlPanelMappings;
	const bindMobileSearch = config.bindMobileSearch === true;
	const filtersWrapperId = config.filtersWrapperId || DEFAULT_FILTERS_WRAPPER_ID;
	const filtersDesktopSlotId = config.filtersDesktopSlotId || DEFAULT_FILTERS_DESKTOP_SLOT_ID;
	const mobileOverlayId = config.mobileOverlayId || DEFAULT_MOBILE_OVERLAY_ID;
	const mobileFacetMountId = config.mobileFacetMountId || DEFAULT_MOBILE_FACET_MOUNT_ID;

	function getSection() {
		return document.querySelector( sectionSelector );
	}

	function moveFiltersToMobileMount() {
		const wrapper = document.getElementById( filtersWrapperId );
		const mount = document.getElementById( mobileFacetMountId );
		if ( wrapper && mount && wrapper.parentElement !== mount ) {
			mount.appendChild( wrapper );
		}
	}

	function resetMobilePanelToggleIcons( $toggle ) {
		const $icons = $toggle.find( '.switch-icon' );
		$icons.removeClass( 'is-active' );
		$icons.eq( 0 ).addClass( 'is-active' );
	}

	function expandMobilePanelToggleIcons( $toggle ) {
		const $icons = $toggle.find( '.switch-icon' );
		$icons.removeClass( 'is-active' );
		$icons.eq( 1 ).addClass( 'is-active' );
	}

	function resetAllMobileOverlayAccordionState() {
		const $overlay = jQuery( '#' + mobileOverlayId );
		if ( ! $overlay.length ) {
			return;
		}
		$overlay.find( '.filter-panel' ).removeClass( 'is-mobile-open' );
		$overlay.find( '.filter-panel-mobile-toggle' ).each( function () {
			const $toggle = jQuery( this );
			resetMobilePanelToggleIcons( $toggle );
			$toggle.attr( 'aria-expanded', 'false' );
		} );
	}

	function showFilterPanelsForMobile( section ) {
		if ( ! section ) {
			return;
		}
		jQuery( section )
			.find( '#' + mobileOverlayId + ' .filter-panel' )
			.stop( true, true )
			.removeClass( 'hidden' )
			.show()
			.css( { display: 'block', height: 'auto', overflow: 'visible' } );
		resetAllMobileOverlayAccordionState();
	}

	function resetFilterPanelsForDesktop( section ) {
		if ( ! section ) {
			return;
		}
		const $section = jQuery( section );
		$section.find( '.filter-checkbox-trigger' ).removeClass( 'is-filter-open' ).attr( 'aria-expanded', 'false' );
		$section.find( '.filter-panel' ).removeClass( 'is-filter-panel-expanded is-mobile-open' );
		$section.find( '.filter-panel-mobile-toggle' ).each( function () {
			resetMobilePanelToggleIcons( jQuery( this ) );
			jQuery( this ).attr( 'aria-expanded', 'false' );
		} );
		$section.find( '.filter-panel' ).stop( true, true ).hide().addClass( 'hidden' ).css( { height: '', overflow: '' } );
		$section.find( '.filter-checkbox-trigger' ).each( function () {
			const $icons = jQuery( this ).find( '.switch-icon' );
			$icons.removeClass( 'is-active' );
			$icons.eq( 0 ).addClass( 'is-active' );
		} );
	}

	function moveFiltersToDesktopSlot() {
		const wrapper = document.getElementById( filtersWrapperId );
		const slot = document.getElementById( filtersDesktopSlotId );
		if ( wrapper && slot && wrapper.parentElement !== slot ) {
			slot.appendChild( wrapper );
		}
	}

	function focusSearchInput() {
		const section = getSection();
		if ( ! section ) {
			return;
		}
		const input = section.querySelector( '.filter-search-inner input.facetwp-search, .filter-search-inner input[type="search"], .filter-search-inner input[type="text"]' );
		if ( input ) {
			input.focus();
			input.scrollIntoView( { behavior: 'smooth', block: 'center' } );
		}
	}

	function toggleSwitchIcons( $container ) {
		$container.find( '.switch-icon' ).each( function () {
			const $icon = jQuery( this );
			if ( $icon.hasClass( 'is-active' ) ) {
				$icon.removeClass( 'is-active' );
			} else {
				$icon.addClass( 'is-active' );
			}
		} );
	}

	function syncFilterTriggerOpenState() {
		const section = getSection();
		if ( ! section ) {
			return;
		}
		const $section = jQuery( section );
		$section.find( '.filter-panel' ).removeClass( 'is-filter-panel-expanded' );
		$section.find( '.filter-checkbox-trigger[data-target]' ).each( function () {
			const $btn = jQuery( this );
			const target = $btn.data( 'target' );
			const $panel = $section.find( '.filter-panel[data-target="' + target + '"]' );
			if ( $panel.length && $panel.is( ':visible' ) ) {
				$btn.addClass( 'is-filter-open' ).attr( 'aria-expanded', 'true' );
				$panel.addClass( 'is-filter-panel-expanded' );
			} else {
				$btn.removeClass( 'is-filter-open' ).attr( 'aria-expanded', 'false' );
			}
		} );
	}

	function openFilterFromUrl( url, param, target ) {
		if ( ! url.includes( param ) ) {
			return;
		}

		const section = getSection();
		if ( ! section ) {
			return;
		}

		jQuery( section ).find( `.filter-panel[data-target='${target}']` ).show().removeClass( 'hidden' );

		jQuery( section ).find( '.filter-checkbox-trigger' ).each( function () {
			const $wrapper = jQuery( this );
			if ( $wrapper.data( 'target' ) === target ) {
				toggleSwitchIcons( $wrapper );
			}
		} );
	}

	function applyUrlOpenPanels() {
		const url = window.location.href;
		let i = 0;
		for ( i = 0; i < urlPanelMappings.length; i++ ) {
			const mapping = urlPanelMappings[ i ];
			openFilterFromUrl( url, mapping.param, mapping.target );
		}
		syncFilterTriggerOpenState();
	}

	function bindMobileOverlay( mobileOverlay, htmlElement, bodyElement, openClass, closeClass, openHandler, closeHandler ) {
		if ( ! mobileOverlay ) {
			return;
		}

		const openButtons = document.getElementsByClassName( openClass );
		let i = 0;
		for ( i = 0; i < openButtons.length; i++ ) {
			openButtons[ i ].addEventListener( 'click', openHandler );
		}

		const closeButtons = document.getElementsByClassName( closeClass );
		for ( i = 0; i < closeButtons.length; i++ ) {
			closeButtons[ i ].addEventListener( 'click', closeHandler );
		}
	}

	function updateFilterCounts() {
		const section = getSection();
		if ( ! section ) {
			return;
		}

		const activeLength = jQuery( section ).find( '.facetwp-checkbox.checked' ).length;
		const $allActive = jQuery( '.filter-count-all-active' );
		$allActive.text( activeLength );

		if ( activeLength === 0 ) {
			$allActive.hide();
		} else {
			$allActive.show();
		}

		let i = 0;
		for ( i = 0; i < filterTargets.length; i++ ) {
			const filterTarget = filterTargets[ i ];
			const activeFilter = jQuery( `.filter-panel[data-target="${filterTarget}"]` ).find( '.checked' ).length;
			const $count = jQuery( `.filter-checkbox-trigger[data-target="${filterTarget}"] .filter-count-active` );
			const $mobileCount = jQuery( `.filter-panel[data-target="${filterTarget}"] .filter-count-active-mobile` );

			$count.text( activeFilter );
			$mobileCount.text( activeFilter );

			if ( activeFilter === 0 ) {
				$count.hide();
				$mobileCount.hide();
			} else {
				$count.show();
				$mobileCount.show();
			}
		}
	}

	function bindMobileFilters() {
		const mobileFilter = document.getElementById( mobileOverlayId );
		const htmlElement = document.getElementsByTagName( 'html' )[ 0 ];
		const bodyElement = document.getElementsByTagName( 'body' )[ 0 ];

		const openMobileFilters = function () {
			moveFiltersToMobileMount();
			const section = getSection();
			if ( section ) {
				showFilterPanelsForMobile( section );
			}
			if ( mobileFilter ) {
				mobileFilter.classList.remove( 'hidden' );
				mobileFilter.classList.add( 'flex', 'flex-col' );
			}
			updateFilterCounts();
			if ( htmlElement ) {
				htmlElement.classList.add( 'overflow-hidden' );
			}
			if ( bodyElement ) {
				bodyElement.classList.add( 'overflow-hidden' );
			}
		};

		const closeMobileFilters = function () {
			const section = getSection();
			if ( section ) {
				resetFilterPanelsForDesktop( section );
			}
			moveFiltersToDesktopSlot();
			if ( section ) {
				applyUrlOpenPanels();
			}
			if ( mobileFilter ) {
				mobileFilter.classList.add( 'hidden' );
				mobileFilter.classList.remove( 'flex', 'flex-col' );
			}
			if ( htmlElement ) {
				htmlElement.classList.remove( 'overflow-hidden' );
			}
			if ( bodyElement ) {
				bodyElement.classList.remove( 'overflow-hidden' );
			}
		};

		const openMobileSearch = function () {
			focusSearchInput();
		};

		bindMobileOverlay(
			mobileFilter,
			htmlElement,
			bodyElement,
			'filter-mobile-open-filter',
			'filter-mobile-close-filter',
			openMobileFilters,
			closeMobileFilters
		);

		if ( bindMobileSearch ) {
			const openSearchButtons = document.getElementsByClassName( 'filter-mobile-open-search' );
			let j = 0;
			for ( j = 0; j < openSearchButtons.length; j++ ) {
				openSearchButtons[ j ].addEventListener( 'click', openMobileSearch );
			}
		}

		const showResultsButton = document.getElementById( 'filter-mobile-show-results' );
		if ( showResultsButton ) {
			showResultsButton.addEventListener( 'click', closeMobileFilters );
		}

		const mq = window.matchMedia( '(min-width: 768px)' );
		const onViewportChange = function () {
			if ( mq.matches && mobileFilter && ! mobileFilter.classList.contains( 'hidden' ) ) {
				closeMobileFilters();
			}
		};
		if ( mq.addEventListener ) {
			mq.addEventListener( 'change', onViewportChange );
		} else if ( mq.addListener ) {
			mq.addListener( onViewportChange );
		}

		jQuery( document ).on( 'click', '#' + mobileOverlayId + ' .filter-panel-mobile-toggle', function ( e ) {
			e.preventDefault();
			const $btn = jQuery( this );
			const $panel = $btn.closest( '.filter-panel' );
			const $overlay = jQuery( '#' + mobileOverlayId );
			if ( ! $overlay.length || ! $overlay[ 0 ].contains( $btn[ 0 ] ) ) {
				return;
			}
			const wasOpen = $panel.hasClass( 'is-mobile-open' );
			$overlay.find( '.filter-panel' ).removeClass( 'is-mobile-open' );
			$overlay.find( '.filter-panel-mobile-toggle' ).each( function () {
				const $t = jQuery( this );
				resetMobilePanelToggleIcons( $t );
				$t.attr( 'aria-expanded', 'false' );
			} );
			if ( ! wasOpen ) {
				$panel.addClass( 'is-mobile-open' );
				expandMobilePanelToggleIcons( $btn );
				$btn.attr( 'aria-expanded', 'true' );
			}
		} );
	}

	function bindDesktopFilters() {
		jQuery( sectionSelector + ' .filter-checkbox-trigger[data-target]' ).on( 'click', function () {
			const $wrapper = jQuery( this );
			toggleSwitchIcons( $wrapper );

			const target = $wrapper.data( 'target' );
			const syncFn = function () {
				syncFilterTriggerOpenState();
			};
			jQuery( sectionSelector + ' .filter-panel[data-target]' )
				.filter( function () {
					return jQuery( this ).data( 'target' ) !== target;
				} )
				.slideUp( 'fast', function () {
					jQuery( this ).addClass( 'hidden' );
					syncFn();
				} );
			jQuery( sectionSelector + ' .filter-panel[data-target]' )
				.filter( function () {
					return jQuery( this ).data( 'target' ) === target;
				} )
				.slideToggle( 'fast', function () {
					const $p = jQuery( this );
					if ( $p.is( ':visible' ) ) {
						$p.removeClass( 'hidden' );
					} else {
						$p.addClass( 'hidden' );
					}
					syncFn();
				} );
		} );

		applyUrlOpenPanels();
	}

	document.addEventListener( 'DOMContentLoaded', function () {
		if ( ! getSection() ) {
			return;
		}
		bindMobileFilters();
	} );

	jQuery( document ).ready( function () {
		if ( ! getSection() ) {
			return;
		}

		bindDesktopFilters();

		document.addEventListener( 'facetwp-loaded', function () {
			if ( ! getSection() ) {
				return;
			}
			updateFilterCounts();
			syncFilterTriggerOpenState();
		} );
	} );
}
